import { useEffect, useState } from 'react';

/** Far enough down that micro-scrolls and iOS rubber-banding never trip the morph. */
const THRESHOLD = 80;

/**
 * Drives the header's bar → floating-pill morph.
 *
 * Three deliberate choices, all about not fighting the scroll thread:
 * - `{ passive: true }` tells the browser this listener will never preventDefault, so it
 *   doesn't have to wait on us before scrolling.
 * - rAF throttle caps the work at one read per paint instead of one per scroll event.
 * - The boolean-flip guard means the class is written once per state change, not on every
 *   frame — without it React re-renders the whole header continuously while scrolling.
 */
export function useFloatingNav(): boolean {
  const [isFloating, setIsFloating] = useState(false);

  useEffect(() => {
    let ticking = false;
    let floating = window.scrollY > THRESHOLD;
    setIsFloating(floating);

    const update = () => {
      const next = window.scrollY > THRESHOLD;
      if (next !== floating) {
        floating = next;
        setIsFloating(next);
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return isFloating;
}
